import {
  ConversationStatus,
  InterviewModality,
  ScreenerAnswer,
  VoiceProvider
} from '@/types/pulse';
import {
  Conversation,
  ConversationMetadata,
  ConversationWCodes,
  ConversationWInterview,
  Transcript
} from '@/types/pulsedb';
import {
  compileConversationFilters,
  ConversationFilter
} from '@/utils/pulse/conversationFilters';
import { SupabaseClient } from '@supabase/supabase-js';
import { cache } from 'react';

export const getConversations = cache(
  async (
    supabase: SupabaseClient,
    interviewId?: number,
    filters: Record<string, unknown> = {},
    limit: number = 1000
  ): Promise<Conversation[] | null> => {
    if (interviewId) {
      // Respondents do not need to filter by interview_id
      filters.interview_id = interviewId;
    }
    const { data } = await supabase
      .from('conversations')
      .select('*')
      .match(filters)
      .eq('deleted', false)
      .eq('hidden', false)
      .order('id', { ascending: false })
      .limit(limit)
      .throwOnError();
    return data;
  }
);

export const getConversationsWithCodes = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number,
    filters: Record<string, unknown> = {}
  ): Promise<ConversationWCodes[] | null> => {
    const { data } = await supabase
      .from('conversations')
      .select('*, conversation_codes (codetype_id, value)')
      .match({ interview_id: interviewId, ...filters })
      .eq('deleted', false)
      .eq('hidden', false)
      .order('id', { ascending: false })
      .throwOnError();
    return data as unknown as ConversationWCodes[];
  }
);

/**
 * One page of an interview's conversations, narrowed by filters the database
 * applies. Every filter reaches the query, so the rows and the count they are
 * paged by answer the same question.
 */
export const getConversationsWithCodesPaginated = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number,
    filters: ConversationFilter[],
    page: number,
    pageSize: number
  ): Promise<{ data: ConversationWCodes[] | null; total: number }> => {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const { embeds, conditions } = compileConversationFilters(filters);

    // The unfiltered embed carries every signal a conversation holds, for the
    // columns; the filters join it again, once each, to narrow by them.
    let query = supabase
      .from('conversations')
      .select(
        ['*', 'conversation_codes (codetype_id, value)', ...embeds].join(', '),
        { count: 'exact' }
      )
      .eq('interview_id', interviewId)
      .eq('deleted', false)
      .eq('hidden', false);

    for (const condition of conditions) {
      query = query.filter(condition.path, condition.op, condition.value);
    }

    const { data, count } = await query
      .order('id', { ascending: false })
      .range(from, to)
      .throwOnError();
    return {
      data: data as unknown as ConversationWCodes[],
      total: count || 0
    };
  }
);

export const getConversationWithCodes = cache(
  async (
    supabase: SupabaseClient,
    conversationId: number
  ): Promise<ConversationWCodes | null> => {
    const { data } = await supabase
      .from('conversations')
      .select('*, conversation_codes (codetype_id, value)')
      .eq('id', conversationId)
      .eq('deleted', false)
      .single()
      .throwOnError();
    return data as unknown as ConversationWCodes;
  }
);

export const getConversation = cache(
  async (
    supabase: SupabaseClient,
    conversationId: number
  ): Promise<Conversation | null> => {
    const { data } = await supabase
      .from('conversations')
      .select('*')
      .eq('id', conversationId)
      .eq('deleted', false)
      .single()
      .throwOnError();
    return data;
  }
);

export const getConversationBySlug = cache(
  async (
    supabase: SupabaseClient,
    slug: string
  ): Promise<Conversation | null> => {
    const { data } = await supabase
      .from('conversations')
      .select('*')
      .eq('slug', slug)
      .eq('deleted', false)
      .single()
      .throwOnError();
    return data;
  }
);

/**
 * Look up a live conversation by its LiveKit room name. The agent stores the
 * room name in `livekit_room_name` at conversation creation, so this is the
 * join the egress-ended webhook uses to map a finished recording back to its
 * conversation. Returns null when no non-deleted conversation matches.
 */
export const getConversationByRoomName = cache(
  async (
    supabase: SupabaseClient,
    roomName: string
  ): Promise<Conversation | null> => {
    const { data } = await supabase
      .from('conversations')
      .select('*')
      .eq('livekit_room_name', roomName)
      .eq('deleted', false)
      .limit(1)
      .maybeSingle()
      .throwOnError();
    return data;
  }
);

export const getConversationCodes = cache(
  async (
    supabase: SupabaseClient,
    conversationId: number,
    filters: Record<string, unknown> = {}
  ): Promise<import('@/types/pulsedb').ConversationCodeWType[] | null> => {
    const { data } = await supabase
      .from('conversation_codes')
      .select('*, interview_codetypes!inner(*)')
      .match({ conversation_id: conversationId, ...filters })
      .eq('interview_codetypes.deleted', false)
      .eq('interview_codetypes.hidden', false)
      .order('id', { ascending: false })
      .throwOnError();

    // if (error) console.log('Error', error);
    // console.log('data', data);
    return data;
  }
);

export const getCountConversations = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number,
    filters?: Record<string, unknown>
  ): Promise<number | null> => {
    const criteria = { interview_id: interviewId, ...filters };
    const { count } = await supabase
      .from('conversations')
      .select('*', { count: 'exact', head: true })
      .match(criteria)
      .eq('deleted', false)
      .eq('hidden', false)
      .throwOnError();
    return count;
  }
);

export const getOrgRecentConversations = cache(
  async (
    supabase: SupabaseClient,
    workspaceId: string,
    interviewId?: number,
    limit: number = 8
  ): Promise<ConversationWInterview[] | null> => {
    let query = supabase
      .from('conversations')
      .select('*, interviews!inner(*)')
      .eq('interviews.workspace_id', workspaceId)
      .eq('interviews.deleted', false)
      .eq('status', ConversationStatus.COMPLETED)
      .eq('deleted', false)
      .eq('hidden', false)
      .order('starred', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(limit);

    if (interviewId) {
      query = query.eq('interview_id', interviewId);
    }
    const { data } = await query.throwOnError();
    return data as unknown as ConversationWInterview[];
  }
);

export const createConversation = async (
  supabase: SupabaseClient,
  interviewId: number,
  queryParams?: Record<string, unknown>,
  voiceProvider?: VoiceProvider,
  modality?: InterviewModality,
  options?: {
    status?: ConversationStatus;
    deviceType?: number | null;
    respondentId?: number | null;
    screenerCompletes?: ScreenerAnswer[];
    /**
     * Set only by callers that run the interview themselves. A row created
     * ahead of the interview leaves this unset for the agent to fill in when
     * it starts.
     */
    startedAt?: string;
    /** The variant the respondent opened, where they opened one. */
    interviewVariantId?: number | null;
  }
) => {
  const { data } = await supabase
    .from('conversations')
    .insert({
      interview_id: interviewId,
      query_params: queryParams,
      voice_provider: voiceProvider,
      modality: modality,
      status: options?.status ?? ConversationStatus.PENDING,
      ...(options?.deviceType != null
        ? { device_type: options.deviceType }
        : {}),
      ...(options?.respondentId != null
        ? { respondent_id: options.respondentId }
        : {}),
      ...(options?.screenerCompletes
        ? { screener_completes: options.screenerCompletes as any }
        : {}),
      ...(options?.startedAt ? { started_at: options.startedAt } : {}),
      ...(options?.interviewVariantId != null
        ? { interview_variant_id: options.interviewVariantId }
        : {})
    })
    .select('*')
    .throwOnError();
  if (data) return data[0] as Conversation | null;
  return null;
};

export const updateConversation = async (
  supabase: SupabaseClient,
  conversationId: number,
  data: Partial<Conversation>
) => {
  const { data: conversation } = await supabase
    .from('conversations')
    .update(data)
    .eq('id', conversationId)
    .select('*')
    .throwOnError();
  if (conversation) return conversation[0] as Conversation | null;
  return null;
};

export const deleteConversation = async (
  supabase: SupabaseClient,
  conversationId: number
) => {
  await supabase
    .from('conversations')
    .update({ deleted: true })
    .eq('id', conversationId)
    .throwOnError();
};

export const getConversationByInterviewConversationId = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number,
    interviewConversationId: number
  ): Promise<Conversation | null> => {
    const { data } = await supabase
      .from('conversations')
      .select('*')
      .eq('interview_id', interviewId)
      .eq('interview_conversation_id', interviewConversationId)
      .eq('deleted', false)
      .eq('hidden', false)
      .maybeSingle()
      .throwOnError();
    return data;
  }
);

export const getTranscript = cache(
  async (
    supabase: SupabaseClient,
    conversationId: number
  ): Promise<Transcript | null> => {
    const { data } = await supabase
      .from('transcripts')
      .select('*')
      .eq('conversation_id', conversationId)
      .maybeSingle()
      .throwOnError();
    return data;
  }
);

export const getConversationMetadata = cache(
  async (
    supabase: SupabaseClient,
    conversationId: number
  ): Promise<ConversationMetadata | null> => {
    const { data } = await supabase
      .from('conversation_metadata')
      .select('*')
      .eq('conversation_id', conversationId)
      .maybeSingle()
      .throwOnError();
    return data;
  }
);

/**
 * Completed, not deleted/hidden conversations for an interview, ordered by id.
 */
export const getCompletedConversations = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number
  ): Promise<Conversation[]> => {
    const { data } = await supabase
      .from('conversations')
      .select('*')
      .eq('interview_id', interviewId)
      .eq('status', ConversationStatus.COMPLETED)
      .eq('deleted', false)
      .eq('hidden', false)
      .order('id')
      .throwOnError();
    return data ?? [];
  }
);

/**
 * How many completed, not deleted/hidden conversations an interview has —
 * counted in the database rather than by fetching the rows, so it stays cheap
 * on interviews with a lot of them.
 *
 * Every conversation on the interview counts, whichever link brought it in.
 * Naming a variant narrows the count to the ones that came through that
 * variant's link, so a variant can be measured against its own cap without
 * leaving the interview's total.
 */
export const getCountCompletedConversations = cache(
  async (
    supabase: SupabaseClient,
    interviewId: number,
    variantId?: number
  ): Promise<number> => {
    const query = supabase
      .from('conversations')
      .select('id', { count: 'exact', head: true })
      .eq('interview_id', interviewId)
      .eq('status', ConversationStatus.COMPLETED)
      .eq('deleted', false)
      .eq('hidden', false);

    const scoped =
      variantId != null ? query.eq('interview_variant_id', variantId) : query;

    const { count } = await scoped.throwOnError();
    return count ?? 0;
  }
);

/** Completed, not deleted/hidden conversations for one respondent, ordered by id. */
export const getCompletedRespondentConversations = cache(
  async (
    supabase: SupabaseClient,
    respondentId: number
  ): Promise<Conversation[]> => {
    const { data } = await supabase
      .from('conversations')
      .select('*')
      .eq('respondent_id', respondentId)
      .eq('status', ConversationStatus.COMPLETED)
      .eq('deleted', false)
      .eq('hidden', false)
      .order('id')
      .throwOnError();
    return data ?? [];
  }
);

/**
 * Updates the existing transcript row for the conversation if present, inserts
 * otherwise.
 */
export const upsertTranscript = async (
  supabase: SupabaseClient,
  conversationId: number,
  fields: Partial<Transcript>
): Promise<void> => {
  const { data: existing } = await supabase
    .from('transcripts')
    .select('id')
    .eq('conversation_id', conversationId)
    .limit(1)
    .maybeSingle()
    .throwOnError();

  if (!existing) {
    await supabase
      .from('transcripts')
      .insert({ conversation_id: conversationId, ...fields })
      .throwOnError();
  } else {
    await supabase
      .from('transcripts')
      .update(fields)
      .eq('conversation_id', conversationId)
      .throwOnError();
  }
};

export interface FunnelConversation {
  id: number;
  interview_conversation_id: number | null;
  created_at: string;
  started_at: string | null;
  ended_at: string | null;
  status: number | null;
  respondent_id: number;
  respondents: { panelist_id: string } | null;
}

/**
 * All respondent-linked, not deleted/hidden conversations for an interview,
 * with each respondent's panelist id — the start/complete side of the
 * Analytics funnel. Fetched in pages to stay under the PostgREST row cap.
 */
export const getFunnelConversations = async (
  supabase: SupabaseClient,
  interviewId: number
): Promise<FunnelConversation[]> => {
  const pageSize = 1000;
  const rows: FunnelConversation[] = [];
  for (let from = 0; ; from += pageSize) {
    const { data } = await supabase
      .from('conversations')
      // !inner is what makes the deleted filter below drop the row. On a plain
      // embed PostgREST applies the filter to the embedded resource only, so a
      // deleted respondent's conversation survives with `respondents: null`.
      .select(
        'id, interview_conversation_id, created_at, started_at, ended_at, status, respondent_id, respondents!inner(panelist_id)'
      )
      .eq('interview_id', interviewId)
      .not('respondent_id', 'is', null)
      .eq('respondents.deleted', false)
      .eq('deleted', false)
      .eq('hidden', false)
      .order('id', { ascending: true })
      .range(from, from + pageSize - 1)
      .throwOnError();
    const page = (data ?? []) as unknown as FunnelConversation[];
    rows.push(...page);
    if (page.length < pageSize) return rows;
  }
};
